import { useEffect } from 'react';
import { useMapMode } from 'hooks/useMapMode';
import type { TimelinePlayback } from 'features/mapping/mapping.types';
import type { TimelineLayerId } from 'features/mapping/mapping.constants';
import { useRadarTimeline } from 'features/mapping/components/layers/RadarLayer';
import { useLightningTimeline } from 'features/mapping/components/layers/LightningLayer';
import { useTimelineLayerList } from 'features/mapping/hooks/useTimelineLayerList';
import { TimelineOverlay } from 'features/mapping/components/timeline/TimelineOverlay';
import { useSetTimelineClock } from 'features/mapping/state/useTimelineClockState';
import { TIMELINE_LAYER_LEGENDS } from './TimelineLayers.constants';

type TimelineLayersProps = {
  onClose: () => void;
};

const EMPTY_PLAYBACK: TimelinePlayback = {
  isLoading: true,
  frames: [],
  currentFrameIndex: 0,
  isPlaying: false,
  onFrameChange: () => {},
  onPlayPause: () => {},
};

/** Wraps the timeline controls and related data sources. */
export const TimelineLayers = ({
  onClose,
}: TimelineLayersProps): JSX.Element | null => {
  const { mapMode, setMapMode } = useMapMode();
  const layers = useTimelineLayerList();
  const isRadarActive = mapMode === 'radar';
  const isLightningActive = mapMode === 'lightning';

  const radar = useRadarTimeline({ visible: isRadarActive, onError: onClose });
  const lightning = useLightningTimeline({
    visible: isLightningActive,
    onError: onClose,
  });

  const playbackByLayer: Record<TimelineLayerId, TimelinePlayback> = {
    radar,
    smoke: EMPTY_PLAYBACK,
    lightning,
  };

  const layer = layers.find(({ id }) => id === mapMode);
  const playback = layer ? playbackByLayer[layer.id] : null;

  const activeLayerId = layer?.id ?? null;
  const frameTimeMs =
    playback?.frames[playback.currentFrameIndex]?.timestamp.getTime() ?? null;

  const setTimelineClock = useSetTimelineClock();

  useEffect(() => {
    setTimelineClock(
      activeLayerId ? { layerId: activeLayerId, timeMs: frameTimeMs } : null,
    );
  }, [activeLayerId, frameTimeMs, setTimelineClock]);

  useEffect(() => () => setTimelineClock(null), [setTimelineClock]);

  if (!layer || !playback) return null;

  const legend = TIMELINE_LAYER_LEGENDS[layer.id];

  return (
    <TimelineOverlay
      playback={playback}
      layer={layer}
      layers={layers}
      onLayerChange={setMapMode}
      legend={legend && <legend.Legend isLoading={playback.isLoading} />}
      legendItemCount={legend?.itemCount}
      onClose={onClose}
    />
  );
};
